import _ from 'lodash';

import { pickDateSettings, withoutUndefined } from './dateSettings';

// A saved filter's settings the list reads from it besides its filters: an
// item's own dates or comparison, which Studio's items don't have
const ITEM_SAVED_FILTER_EXTRAS: (keyof LegacySavedFilter)[] = [
  'dateRange',
  'relativeDateRange',
  'advancedRelativeDateRange',
  'comparison',
  'mustExistFilters',
  'mustNotExistFilters',
];

// The list's comparison as a block's: the previous period (by its length,
// or a whole interval) or last year. Undefined without one; null for one
// Studio can't show (e.g. against a window of earlier days).
const toBlockComparison = (
  comparison: PersistedComparisonType | undefined,
): Studio.BlockComparison | undefined | null => {
  if (!comparison) {
    return undefined;
  }
  const { compareType, compareByInterval, ...rest } = comparison;
  if (!_.isEmpty(_.omitBy(rest, _.isUndefined))) {
    return null;
  }
  if (compareType === 'lastYear' && !compareByInterval) {
    return { compareType: 'lastYear' };
  }
  if (compareType === 'previous') {
    return compareByInterval && compareByInterval !== 'day'
      ? { compareType: 'previous', interval: compareByInterval }
      : { compareType: 'previous' };
  }
  return null;
};

// A saved Metric List as a metric list Table, its items in the order the
// list draws them (items left out of its order aren't drawn, so they're
// left out). Each item's filters come from its saved filter. Opens as a
// Classic block when Studio couldn't save it back unchanged: an item whose
// saved filter isn't found or has dates of its own, or a comparison Studio
// can't show.
export const readMetricList = ({
  list,
  savedFilters,
}: {
  list: MetricListGadgetType;
  savedFilters: Record<string, LegacySavedFilter | undefined>;
}): { content: Omit<Studio.Card, 'id' | 'layout'> } | { reason: string } => {
  const comparison = toBlockComparison(list.comparison);
  if (comparison === null) {
    return { reason: 'Uses: Comparison' };
  }
  const ordered = (list.listOrder ?? [])
    .map((id) => list.list.find((item) => item.id === id))
    .filter((item): item is MetricListItemType => !!item);
  const items: Studio.TableMetricListItem[] = [];
  for (const item of ordered) {
    const savedFilter = item.savedFilterId
      ? savedFilters[item.savedFilterId]
      : undefined;
    if (!savedFilter) {
      return { reason: 'Metric list: filters not found' };
    }
    if (
      ITEM_SAVED_FILTER_EXTRAS.some((key) => {
        const value = savedFilter[key];
        return Array.isArray(value) ? value.length > 0 : !!value;
      })
    ) {
      return { reason: 'Metric list: filters with dates of their own' };
    }
    items.push(
      withoutUndefined({
        id: item.id,
        metricId: item.metricId,
        name: item.name || undefined,
        filters: savedFilter.scope?.length ? savedFilter.scope : undefined,
        drillDowns: savedFilter.drillDowns?.length
          ? savedFilter.drillDowns
          : undefined,
        savedFilterId: savedFilter.id,
        reportDrillDownId: item.reportDrillDownId || undefined,
        boardDrillDownId: item.boardDrillDownId || undefined,
        isEntityFilterEnabled: item.isEntityFilterEnabled || undefined,
        isDefaultFiltersDisabled: item.isDefaultFiltersDisabled || undefined,
      }),
    );
  }
  const gadgetSettings = withoutUndefined({
    isBonusPeriodMode: list.isBonusPeriodMode || undefined,
    weekStartsOnOverride: list.weekStartsOnOverride,
  });
  return {
    content: withoutUndefined({
      visType: 'table' as const,
      name: list.name || undefined,
      table: {
        rows: 'metricList' as const,
        metricList: { metricListId: list.id, items },
      },
      dateSettings: list.dateField ? pickDateSettings(list) : undefined,
      comparison,
      gadgetSettings: _.isEmpty(gadgetSettings) ? undefined : gadgetSettings,
    }),
  };
};
